// Lists that open fast (brief 1E-32, DECISIONS 11/01 "Records that fit on one screen"): every list
// has a search box over its main columns, the column filters it had, and pages of 20, 50 or 100
// with Previous, Next and "41–60 of 312". The server answers one page with the total; the screen
// draws what comes back. Pure: the routes read their rows through the one query helper (so every
// row is the company's own), then this narrows, sorts and cuts the page.

export const PER_PAGE = [20, 50, 100] as const;
export type PerPage = (typeof PER_PAGE)[number];
export const DEFAULT_PER: PerPage = 20;

export type SortDir = "ascending" | "descending";

/** Per column: text it must contain, or one value picked from the column's values. */
export interface ColumnFilter {
  text?: string;
  pick?: string;
}

export interface ListQuery {
  /** The search box: every word must be found in one of the searched columns. */
  q: string;
  page: number;
  per: PerPage;
  sort: { col: string; dir: SortDir } | null;
  filters: Record<string, ColumnFilter>;
  /** The stage chip picked over the list; null is "All". */
  chip: string | null;
}

export const EMPTY_QUERY: ListQuery = {
  q: "",
  page: 1,
  per: DEFAULT_PER,
  sort: null,
  filters: {},
  chip: null,
};

/** What paging needs from a column: its id, the text a row shows in it, and whether search reads it. */
export interface PageColumn<T> {
  id: string;
  text: (row: T) => string;
  /** The search box reads this column (the list's main columns: name, number, who, what). */
  search?: boolean;
  /** The filter row picks from this column's values (sent with the page). */
  pick?: boolean;
}

const ID = /^[a-z][a-zA-Z0-9_]{0,39}$/;
const clip = (s: string, n: number) => s.slice(0, n);

export const perOf = (v: unknown): PerPage =>
  PER_PAGE.find((p) => String(p) === String(v)) ?? DEFAULT_PER;

/**
 * The list's part of an address: q, page, per, sort and dir, f.<col> (contains), p.<col> (picked)
 * and chip. Anything else, or a malformed value, falls back to the default.
 */
export function readListQuery(params: URLSearchParams): ListQuery {
  const page = Math.floor(Number(params.get("page")));
  const col = params.get("sort");
  const dir = params.get("dir") === "desc" ? "descending" : "ascending";
  const filters: Record<string, ColumnFilter> = {};
  for (const [k, v] of params) {
    const m = /^([fp])\.(.+)$/.exec(k);
    const col = m?.[2];
    if (!m || !col || !ID.test(col) || !v.trim()) continue;
    const f = (filters[col] ??= {});
    if (m[1] === "f") f.text = clip(v, 120);
    else f.pick = clip(v, 120);
  }
  const chip = params.get("chip");
  return {
    q: clip(params.get("q")?.trim() ?? "", 120),
    page: Number.isFinite(page) && page >= 1 ? page : 1,
    per: perOf(params.get("per")),
    sort: col && ID.test(col) ? { col, dir } : null,
    filters,
    chip: chip === null ? null : clip(chip, 120),
  };
}

/** The same query as an address's search part (what the screen sends). */
export function listParams(q: ListQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.q.trim()) p.set("q", q.q.trim());
  p.set("page", String(q.page));
  p.set("per", String(q.per));
  if (q.sort) {
    p.set("sort", q.sort.col);
    p.set("dir", q.sort.dir === "descending" ? "desc" : "asc");
  }
  for (const [col, f] of Object.entries(q.filters)) {
    if (f.text?.trim()) p.set(`f.${col}`, f.text.trim());
    if (f.pick) p.set(`p.${col}`, f.pick);
  }
  if (q.chip !== null) p.set("chip", q.chip);
  return p;
}

const DATE = /^(\d{2})\/(\d{2})\/(\d{4})/;
function sortKey(text: string): number | string {
  const d = DATE.exec(text);
  if (d) return Date.UTC(Number(d[3]), Number(d[1]) - 1, Number(d[2]));
  if (/^[\s$\d.,%-]+$/.test(text) && /\d/.test(text)) {
    const n = Number(text.replace(/[$,%\s]/g, ""));
    if (!Number.isNaN(n)) return n;
  }
  return text.toLowerCase();
}

/** Dates as dates, numbers and money as numbers, words as words. */
export function compareText(a: string, b: string): number {
  const x = sortKey(a);
  const y = sortKey(b);
  if (typeof x === "number" && typeof y === "number") return x - y;
  return String(x).localeCompare(String(y), "en-US", { numeric: true });
}

/** Whether a row passes every column filter. */
export function passesFilters<T>(
  row: T,
  columns: readonly PageColumn<T>[],
  filters: ListQuery["filters"],
): boolean {
  return columns.every((c) => {
    const f = filters[c.id];
    if (!f) return true;
    const text = c.text(row);
    if (f.pick && text !== f.pick) return false;
    if (f.text?.trim() && !text.toLowerCase().includes(f.text.trim().toLowerCase())) return false;
    return true;
  });
}

/** Whether a row matches the search box: every word is in one of the searched columns. */
export function matchesSearch<T>(row: T, columns: readonly PageColumn<T>[], q: string): boolean {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const searched = columns.filter((c) => c.search);
  const hay = (searched.length ? searched : columns)
    .map((c) => c.text(row).toLowerCase())
    .join(" ");
  return words.every((w) => hay.includes(w));
}

/** Rows the search and the column filters let through, before the chip (the chips count these). */
export function narrowed<T>(
  rows: readonly T[],
  columns: readonly PageColumn<T>[],
  q: Pick<ListQuery, "q" | "filters">,
): T[] {
  return rows.filter((r) => matchesSearch(r, columns, q.q) && passesFilters(r, columns, q.filters));
}

/** Sorted by the picked column, ties kept in the order they came. */
export function sorted<T>(
  rows: readonly T[],
  columns: readonly PageColumn<T>[],
  sort: ListQuery["sort"],
): T[] {
  const col = sort && columns.find((c) => c.id === sort.col);
  if (!sort || !col) return rows.slice();
  const dir = sort.dir === "ascending" ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => dir * compareText(col.text(a.r), col.text(b.r)) || a.i - b.i)
    .map((x) => x.r);
}

export interface PageInfo {
  /** Rows after search, filters and chip. */
  total: number;
  /** The page shown (the last page when the one asked for is past the end). */
  page: number;
  per: PerPage;
  /** 1-based first and last row on this page; 0 and 0 when there are none. */
  fromRow: number;
  toRow: number;
  pages: number;
  /** Rows after search and filters, before the chip ("All 312"). */
  all: number;
  /** Each chip value that has rows after search and filters. */
  chips: { value: string; count: number }[];
  /** For the filter row's pick lists: each column's values over every row. */
  values: Record<string, string[]>;
}

export interface Page<T> extends PageInfo {
  rows: T[];
}

/**
 * One page of a list: searched, filtered, narrowed to the chip, sorted, then cut. The total and the
 * chip counts are over everything that matched, so the count line and the chips are true.
 */
export function pageOf<T>(
  rows: readonly T[],
  columns: readonly PageColumn<T>[],
  q: ListQuery,
  chips?: { values: readonly string[]; of: (row: T) => string },
): Page<T> {
  const through = narrowed(rows, columns, q);
  let shown = through;
  if (chips && q.chip !== null) shown = shown.filter((r) => chips.of(r) === q.chip);
  shown = sorted(shown, columns, q.sort);
  const total = shown.length;
  const pages = Math.max(1, Math.ceil(total / q.per));
  const page = Math.min(Math.max(1, q.page), pages);
  const start = (page - 1) * q.per;
  const out = shown.slice(start, start + q.per);
  const values: Record<string, string[]> = {};
  for (const c of columns)
    if (c.pick)
      values[c.id] = [...new Set(rows.map((r) => c.text(r)))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
  return {
    rows: out,
    total,
    page,
    per: q.per,
    fromRow: out.length ? start + 1 : 0,
    toRow: out.length ? start + out.length : 0,
    pages,
    all: through.length,
    chips: chips
      ? chips.values
          .map((value) => ({ value, count: through.filter((r) => chips.of(r) === value).length }))
          .filter((c) => c.count > 0)
      : [],
    values,
  };
}

/** The page's part of an answer: everything but the rows, which go under the list's own name. */
export function pageInfo<T>(p: Page<T>): PageInfo {
  const info: PageInfo & { rows?: T[] } = { ...p };
  delete info.rows;
  return info;
}

/** The count line under a list: "41–60 of 312", "1 of 1", or "Nothing" when it's empty. */
export function countLine(p: Pick<PageInfo, "fromRow" | "toRow" | "total">): string {
  if (!p.total) return "Nothing";
  return p.fromRow === p.toRow
    ? `${p.fromRow} of ${p.total}`
    : `${p.fromRow}–${p.toRow} of ${p.total}`;
}

/** A route's rows as one page: reads the address, answers the rows and the page. */
export function pageFromRequest<T>(
  req: Request,
  rows: readonly T[],
  columns: readonly PageColumn<T>[],
  chips?: { values: readonly string[]; of: (row: T) => string },
): Page<T> {
  return pageOf(rows, columns, readListQuery(new URL(req.url).searchParams), chips);
}
